// @flow

import { put, call, all, takeEvery } from "redux-saga/effects";
import type { Action } from "redux-action";

import http from "@http";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import * as actions from "@modules/service/constants/actions";
import actionCreators from "@modules/service/actionCreator";
import type {
  NewManagerSmsNotification,
  ManagerSmsRestriction,
} from "@modules/service/reducer/manager-sms-notifications/types";
import type { HierarchyAssignment } from "@modules/service/types";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ManagerPagerNotificationsListRequest = $Exact<{
  clientId: string,
}>;

export function* onGetPagerRules({
  payload,
}: Action<ManagerPagerNotificationsListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<ManagerSmsRestriction>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/pager-restriction`,
    );

    yield put(actionCreators.GetManagerSmsNotificationsPagerRulesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetManagerSmsNotificationsPagerRulesError(e.response));
  }
}

type AddManagerSmsNotificationPagerRuleRequest = $Exact<{
  clientId: string,
  rule: NewManagerSmsNotification,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onAddPagerRule({
  payload,
}: Action<AddManagerSmsNotificationPagerRuleRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.rule || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const { data: newRule } = yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/pager-restriction`,
      payload.rule,
    );
    if (!newRule) {
      throw Error("Failed to create new rule");
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/pager-restriction/${newRule.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.AddManagerSmsNotificationPagerRuleSuccess()),
      put(actionCreators.GetManagerSmsNotificationsPagerRules({ clientId: payload.clientId })),
      put(actionCreators.SetShowAddManagerSmsNotificationModal(false)),
      put(sendSuccessMessage("PAGER_RESTRICTION_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddManagerSmsNotificationPagerRuleError(e.response));
  }
}

type UpdateManagerSmsNotificationPagerRuleRequest = $Exact<{
  clientId: string,
  rule: ManagerSmsRestriction,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdatePagerRule({
  payload,
}: Action<UpdateManagerSmsNotificationPagerRuleRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.rule || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/pager-restriction/${payload.rule.id}`,
      payload.rule,
    );

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/pager-restriction/${payload.rule.id}/assignment`,
    );
    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignment) => {
          return call(
            http.delete,
            `/api/v1/service-admin/client/${
              payload.clientId
            }/service-config/absence-management/pager-restriction/${
              payload.rule.id
            }/assignment/${a.id || ""}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/pager-restriction/${payload.rule.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.UpdateManagerSmsNotificationPagerRuleSuccess()),
      put(actionCreators.GetManagerSmsNotificationsPagerRules({ clientId: payload.clientId })),
      put(actionCreators.SetShowEditManagerSmsNotificationModal(false)),
      put(sendSuccessMessage("PAGER_RESTRICTION_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateManagerSmsNotificationPagerRuleError(e.response));
  }
}

type DeleteManagerSmsNotificationPagerRuleRequest = $Exact<{
  clientId: string,
  ruleId: number,
}>;

export function* onDeletePagerRule({
  payload,
}: Action<DeleteManagerSmsNotificationPagerRuleRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.ruleId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/pager-restriction/${payload.ruleId}`,
    );

    yield all([
      put(actionCreators.DeleteManagerSmsNotificationPagerRuleSuccess()),
      put(actionCreators.GetManagerSmsNotificationsPagerRules({ clientId: payload.clientId })),
      put(actionCreators.SetShowDeleteManagerSmsNotificationModal(false)),
      put(sendSuccessMessage("PAGER_RESTRICTION_DELETED")),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteManagerSmsNotificationPagerRuleError(e.response));
  }
}

type ManagerPagerNotificationAssignmentsRequest = $Exact<{
  clientId: string,
  ruleId: number,
}>;

export function* onGetPagerAssignments({
  payload,
}: Action<ManagerPagerNotificationAssignmentsRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<HierarchyAssignment>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.ruleId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/pager-restriction/${payload.ruleId}/assignment`,
    );

    yield put(actionCreators.GetManagerSmsNotificationsPagerAssignmentsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetManagerSmsNotificationsPagerAssignmentsError(e.response));
  }
}
// ROOT

export function* ManagerPagerNotificationsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(actions.GetManagerSmsNotificationsPagerRules, onGetPagerRules),
    takeEvery(actions.AddManagerSmsNotificationPagerRule, onAddPagerRule),
    takeEvery(actions.UpdateManagerSmsNotificationPagerRule, onUpdatePagerRule),
    takeEvery(actions.DeleteManagerSmsNotificationPagerRule, onDeletePagerRule),
    takeEvery(actions.GetManagerSmsNotificationsPagerAssignments, onGetPagerAssignments),
  ]);
}

export default ManagerPagerNotificationsSagas;
